/**
 * AdminViewToggle v1.0 (T779, 2026-10-14) — the "Admin view" switch in the top
 * header, next to the account menu. Same pattern on every Fox site.
 *
 * Renders nothing for non-admins (admin is decided server-side; see
 * AdminViewContext). Turning it on opens the numbers page (/admin). Turning it
 * off on an admin page returns to the dashboard; anywhere else it stays put.
 * This click is the only thing that changes the saved choice.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { useAdminView } from "@/components/layout/AdminViewContext";

export const ADMIN_VIEW_TOOLTIP = "See signups, usage and settings for this site";

function isAdminPath(pathname: string) {
  return pathname === "/admin" || pathname.startsWith("/admin/") || pathname.startsWith("/dashboard/admin");
}

export function AdminViewToggle({ tone = "app" }: { tone?: "app" | "admin" }) {
  const { isAdmin, adminView, setAdminView } = useAdminView();
  const navigate = useNavigate();
  const location = useLocation();
  if (!isAdmin) return null;

  const onChange = (on: boolean) => {
    void setAdminView(on);
    if (on) navigate("/admin");
    else if (isAdminPath(location.pathname)) navigate("/dashboard");
  };

  return (
    <label
      title={ADMIN_VIEW_TOOLTIP}
      data-testid="admin-view-toggle"
      className={`flex items-center gap-2 cursor-pointer select-none text-xs font-medium ${
        tone === "admin" ? "text-amber-200" : "text-muted-foreground"
      }`}
    >
      <span className="hidden sm:inline">Admin view</span>
      <Switch
        checked={adminView}
        onCheckedChange={onChange}
        aria-label="Admin view"
        title={ADMIN_VIEW_TOOLTIP}
        className="h-5 w-9 [&>span]:h-4 [&>span]:w-4 [&>span[data-state=checked]]:translate-x-4"
      />
    </label>
  );
}
